// Where a Select dropdown opens, and how tall it is allowed to be.
//
// REF-6158, and the third recurrence of one symptom (REF-5358 → REF-5748 → REF-6081): the
// dropdown opened UPWARD over the header and the left sidebar. The old rule was "flip up when
// the list doesn't fit below" with nothing capping the height, so a long list under a
// `sticky top-0` filter bar flipped into the ~0px of room above it and rendered at its full
// natural height — straight off the top of the page. A call-site `maxHeight` was the only thing
// that prevented it, and 7 of the 61 `<Select>` call sites pass one, so every previous fix only
// moved the bug to the next of the other 54.
//
// Two invariants, enforced here instead of per call site:
//
//   1. Never flip to a side with LESS room than the side we are leaving. "Doesn't fit below"
//      is not a reason to open above — "there is more room above" is.
//   2. The panel is always clamped to the room on the chosen side, so a list taller than the
//      viewport scrolls inside its own border. The result NEVER exceeds that room: a panel that
//      overflows the side it opened toward is the bug, and on a `fixed`/absolute panel no amount
//      of page scrolling brings those options back.
//
// The one exception is `lockDirection` (`forceDirection` at the call site) — see below.
//
// Kept pure (numbers in, numbers out) so the decision is testable without a DOM — see
// selectPlacement.test.ts. The caller measures; this decides.

// Breathing room kept between the panel and the viewport edge. 16px covers the 8px gap the
// dropdown sits off its trigger plus an 8px margin at the far edge, which is what both render
// paths already used.
export const DROPDOWN_VIEWPORT_MARGIN = 16;

// Row metrics for the estimate below. A row is `text-[13px] px-3 py-2`; the search row adds its
// own padding and bottom border; the <ul> carries py-1 plus its border.
const OPTION_ROW_HEIGHT = 36;
const SEARCH_ROW_HEIGHT = 50;
const LIST_CHROME_HEIGHT = 10;

// A stand-in for a list that cannot be measured yet: in portal mode the dropdown is only mounted
// while open, so the first placement pass has no DOM to read. Deliberately rough — Select
// re-resolves against the real height before paint (see the layout effect in Select.tsx). It only
// has to be close enough to pick the right side for a list that clearly fits. Rows carrying an
// `img` or a `description` render taller than OPTION_ROW_HEIGHT, which is another reason the
// measured correction is the thing that has to be right, not this.
export const estimateContentHeight = ({ optionCount, searchable = false }: { optionCount: number; searchable?: boolean }): number =>
  LIST_CHROME_HEIGHT + optionCount * OPTION_ROW_HEIGHT + (searchable ? SEARCH_ROW_HEIGHT : 0);

export interface DropdownPlacementInput {
  // Distance from the trigger's bottom edge to the bottom of the viewport.
  spaceBelow: number;
  // Distance from the top of the viewport to the trigger's top edge.
  spaceAbove: number;
  // The list's natural, unclamped height, as a border box. 0 means "not measured yet".
  contentHeight: number;
  // The call site's own cap, when it has an opinion. Only ever lowers the result.
  maxHeight?: number;
  // The `direction === "top"` preference.
  preferAbove?: boolean;
  // `forceDirection` — the call site owns the placement, so we neither flip nor clamp. Those
  // dropdowns live inside scrollable panels whose own geometry we cannot see from the viewport
  // rect, and they render unbounded today; clamping them to viewport room would squeeze a
  // panel that currently just extends past the fold. Their height stays whatever the call site
  // asked for (or the content).
  lockDirection?: boolean;
}

export interface DropdownPlacement {
  openAbove: boolean;
  maxHeight: number;
}

export const resolveDropdownPlacement = ({ spaceBelow, spaceAbove, contentHeight, maxHeight, preferAbove = false, lockDirection = false }: DropdownPlacementInput): DropdownPlacement => {
  const roomBelow = Math.max(0, spaceBelow - DROPDOWN_VIEWPORT_MARGIN);
  const roomAbove = Math.max(0, spaceAbove - DROPDOWN_VIEWPORT_MARGIN);

  // What we would render given unlimited room. Unmeasured content falls back to the call site's
  // cap (or "as much as fits") rather than to zero — clamping an unmeasured list to 0px would
  // hide the dropdown on its very first open.
  const desired = contentHeight > 0 ? Math.min(contentHeight, maxHeight ?? contentHeight) : maxHeight ?? Number.POSITIVE_INFINITY;

  if (lockDirection) return { openAbove: preferAbove, maxHeight: Number.isFinite(desired) ? desired : preferAbove ? roomAbove : roomBelow };

  const preferredRoom = preferAbove ? roomAbove : roomBelow;
  const oppositeRoom = preferAbove ? roomBelow : roomAbove;

  // Flip only when the preferred side cannot show the whole panel AND the other side has
  // strictly more room. Ties keep the preferred side, so nothing that fits today moves.
  const shouldFlip = preferredRoom < desired && oppositeRoom > preferredRoom;
  const openAbove = shouldFlip ? !preferAbove : preferAbove;

  // `Math.min` last, unconditionally: the cap may never exceed the room on the chosen side (it
  // would render off-screen) and may never exceed what the call site asked for.
  return { openAbove, maxHeight: Math.min(desired, openAbove ? roomAbove : roomBelow) };
};
